import { useState, useEffect, useRef } from "react";
import { MapPin, Search, X, Check, Sparkles, ChevronRight } from "lucide-react";

/**
 * CityQuickFilter - a lightweight city picker for search/filter pages.
 * Uses legacy city_id format (matches /api/cities + ads.city_id).
 *
 * Props:
 *   - cities: [{ id, name, county }]
 *   - value: city_id string (or "all" / "" for no filter)
 *   - onChange: (city_id) => void  ("all" or city.id)
 */
const TOP_CITY_IDS = [
  "bucuresti",
  "cluj",
  "timisoara",
  "iasi",
  "constanta",
  "brasov",
  "craiova",
  "oradea",
];

const CITY_EMOJI = {
  bucuresti: "🏛️",
  cluj: "🎓",
  timisoara: "🏰",
  iasi: "📚",
  constanta: "🌊",
  brasov: "🏔️",
  craiova: "🌳",
  oradea: "🎨",
};

export default function CityQuickFilter({ cities = [], value, onChange }) {
  const [query, setQuery] = useState("");
  const [focused, setFocused] = useState(false);
  const containerRef = useRef(null);

  const selected = cities.find((c) => c.id === value);

  // Close dropdown on outside click
  useEffect(() => {
    if (!focused) return;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setFocused(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [focused]);

  const normalize = (s) =>
    (s || "").toLowerCase()
      .replace(/ă/g, "a").replace(/â/g, "a").replace(/î/g, "i")
      .replace(/ș/g, "s").replace(/ş/g, "s")
      .replace(/ț/g, "t").replace(/ţ/g, "t");

  const filtered = query.trim().length >= 1
    ? cities.filter((c) => normalize(c.name).includes(normalize(query))).slice(0, 8)
    : [];

  const popularCities = TOP_CITY_IDS
    .map((id) => cities.find((c) => c.id === id))
    .filter(Boolean);

  const pick = (cityId) => {
    onChange(cityId);
    setQuery("");
    setFocused(false);
  };

  return (
    <div className="space-y-3" ref={containerRef} data-testid="city-quick-filter">
      {/* Search */}
      <div className="relative">
        <div
          className={`relative rounded-xl border transition-all ${
            focused && filtered.length > 0
              ? "border-blue-500/50 shadow-[0_0_20px_rgba(59,130,246,0.12)]"
              : "border-white/10"
          } bg-[#121212]`}
        >
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
          <input
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setFocused(true);
            }}
            onFocus={() => setFocused(true)}
            placeholder={selected ? selected.name : "Caută oraș..."}
            className="w-full h-11 bg-transparent pl-9 pr-10 text-white text-sm placeholder:text-slate-500 outline-none rounded-xl"
            data-testid="city-filter-search"
            autoComplete="off"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white"
              aria-label="Șterge"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        {focused && filtered.length > 0 && (
          <div className="absolute left-0 right-0 top-full mt-1 z-30 rounded-xl border border-white/10 bg-[#0a0a0a]/95 backdrop-blur-xl shadow-2xl overflow-hidden max-h-64 overflow-y-auto">
            {filtered.map((c, i) => (
              <button
                key={c.id}
                type="button"
                onClick={() => pick(c.id)}
                className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-blue-500/10 transition-colors border-b border-white/5 last:border-0"
                data-testid={`city-filter-result-${c.id}`}
              >
                <MapPin className="w-3.5 h-3.5 text-blue-400 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-white text-sm truncate">{c.name}</div>
                  <div className="text-[10px] text-slate-500 truncate">{c.county}</div>
                </div>
                <ChevronRight className="w-3.5 h-3.5 text-slate-600 flex-shrink-0" />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Selected chip + All toggle */}
      <div className="flex items-center gap-2 flex-wrap">
        <button
          type="button"
          onClick={() => pick("all")}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
            !value || value === "all"
              ? "bg-blue-500/20 border-blue-500/40 text-blue-300"
              : "bg-white/5 border-white/10 text-slate-400 hover:bg-white/10"
          }`}
          data-testid="city-filter-all"
        >
          {(!value || value === "all") && <Check className="w-3 h-3" />}
          Toate orașele
        </button>

        {selected && value !== "all" && (
          <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-emerald-500/20 border border-emerald-500/40 text-emerald-300">
            <MapPin className="w-3 h-3" />
            {selected.name}
            <button
              type="button"
              onClick={() => pick("all")}
              className="ml-1 hover:text-white"
              aria-label="Elimină filtru"
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        )}
      </div>

      {/* Popular chips */}
      {popularCities.length > 0 && !query && (
        <div>
          <div className="flex items-center gap-1.5 mb-2">
            <Sparkles className="w-3 h-3 text-amber-400" />
            <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
              Populare
            </span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {popularCities.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => pick(c.id)}
                className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs transition-all border ${
                  value === c.id
                    ? "bg-blue-500/20 border-blue-500/40 text-blue-300"
                    : "bg-[#0f0f0f] border-white/10 text-slate-300 hover:border-blue-500/30 hover:text-white"
                }`}
                data-testid={`city-filter-popular-${c.id}`}
              >
                <span>{CITY_EMOJI[c.id] || "📍"}</span>
                <span>{c.name}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
